import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Transaction } from '../App';
import { toast } from 'sonner';
import { UsdtDeposit } from './UsdtDeposit';
import { UsdtWithdraw } from './UsdtWithdraw';
import { ArrowUpRight, ArrowDownLeft, Copy, QrCode, Wallet, ArrowLeft, Link, Plus, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { WalletConnectModal } from './WalletConnectModal';
import { TokenDetails } from './TokenDetails';
import { LockChip } from './LockChip';
import { LockupModal } from './LockupModal';
import { TransactionDetailsModal } from './TransactionDetailsModal';
import { AddTokenModal } from './AddTokenModal';
import { TransModal } from './TransModal';
import { BuyConfirmModal } from './BuyConfirmModal';
import { i18n, tb } from '../utils/i18n';
import { tr } from '../utils/localeFormat';

interface WalletTransferProps {
  user: User;
  transactions: Transaction[];
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
}

interface Token {
  symbol: string;
  name: string;
  balance: number;
  value: number;
  change: number;
  icon: string;
}

export function WalletTransfer({ user, transactions, onUpdateBalance, onAddTransaction }: WalletTransferProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  const [activeView, setActiveView] = useState('main');
  // [2026-09-13] BSC(BNB체인) USDT 입금 기능.
  // 백엔드 스위치가 켜져 있을 때만 화면에 노출한다. 꺼져 있으면 기존처럼 "오픈 예정".
  const [bscEnabled, setBscEnabled] = useState(false);
  // 출금은 서버 설정(withdraw_enabled)이 켜졌을 때만 연다. 꺼져 있으면 기존과 동일.
  const [withdrawEnabled, setWithdrawEnabled] = useState(false);
  // 락업된 wHANDY 는 볼트에 있어 지갑 잔액에 안 잡힌다. 따로 받아와 합산해 보여준다.
  const [lockedHandy, setLockedHandy] = useState(0);
  const [lockSchedule, setLockSchedule] = useState([] as { date: string; amount: number }[]);
  /**
   * 락업으로 지급된 건들.
   * 토큰이 금고로 가기 때문에 지갑 Transfer 가 안 남아 거래내역에 아예 안 떴다.
   * 서버에서 받아 거래내역에 합쳐 보여준다.
   */
  const [grants, setGrants] = useState([] as any[]);
  // [2026-09-13 보안] 잔액을 움직이는 API 는 이제 토큰이 필요하다.
  // 예전엔 body 의 address 만 보내서, 로그인 없이 남의 잔액을 조작할 수 있었다.
  const authHeaders = (): Record<string, string> => {
    const t = localStorage.getItem('handy-jwt-token') || '';
    return t
      ? { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` }
      : { 'Content-Type': 'application/json' };
  };
  // 스왑 운영 모드. 'user' 가 아니면 회원이 직접 스왑할 수 없다(어드민 수기 처리).
  const [swapMode, setSwapMode] = useState<string>('');
  // 구매 기능. 어드민이 열어주기 전(buyEnabled=false)에는 버튼이 딤드 상태로 남는다.
  const [buyEnabled, setBuyEnabled] = useState(false);
  const [buyLock, setBuyLock] = useState<any>(null);
  const [buyFee, setBuyFee] = useState(0);
  const [buyPrice, setBuyPrice] = useState<number | null>(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  React.useEffect(() => {
    fetch('/api/bsc/info')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setBscEnabled(!!d?.enabled))
      .catch(() => {});

    // 출금 오픈 여부 (서버가 켜줬을 때만 '보내기' 버튼이 출금으로 열린다)
    (async () => {
      try {
        const t = localStorage.getItem('handy-jwt-token') || '';
        const r = await fetch('/api/withdraw/info',
          t ? { headers: { Authorization: `Bearer ${t}` } } : undefined);
        const d = await r.json();
        setWithdrawEnabled(Boolean(d?.enabled));
      } catch { /* 실패하면 닫힌 상태 유지 */ }
    })();

    // 내 락업 물량 (없으면 0)
    (async () => {
      try {
        const t = localStorage.getItem('handy-jwt-token') || '';
        if (!t) return;
        const r = await fetch('/api/wallet-lockups', { headers: { Authorization: `Bearer ${t}` } });
        const d = await r.json();
        if (d?.success) {
          setLockedHandy(Number(d.totalLocked || 0));
          setLockSchedule(Array.isArray(d.schedule) ? d.schedule : []);
          setGrants(Array.isArray(d.grants) ? d.grants : []);
        }
      } catch { /* 조회 실패는 화면을 막지 않는다 */ }
    })();
    fetch('/api/usdt/swap-mode')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        setSwapMode(d?.mode || '');
        setBuyEnabled(Boolean(d?.buyEnabled));
        setBuyLock(d?.lock || null);
        setBuyFee(Number(d?.feePercent || 0));
      })
      .catch(() => {});
  }, []);
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWalletConnect, setShowWalletConnect] = useState(false);
  const [connectedWallet, setConnectedWallet] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [activeTab, setActiveTab] = useState('crypto');
  const [balanceView, setBalanceView] = useState('vttn');
  
  // 재단 지갑 전송 관련 상태
  const [isFoundationMode, setIsFoundationMode] = useState(false); // 기본값을 false로 변경
  const [transferMode, setTransferMode] = useState('individual');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [foundationAmount, setFoundationAmount] = useState('');
  
  // 새로운 지갑 선택 관련 상태
  const [senderWallet, setSenderWallet] = useState(null); // 보내는 지갑
  const [receiverWallet, setReceiverWallet] = useState(null); // 받는 지갑
  const [senderBalance, setSenderBalance] = useState(0); // 보내는 지갑 잔고
  const [receiverBalance, setReceiverBalance] = useState(0); // 받는 지갑 잔고
  const [searchQuery, setSearchQuery] = useState(''); // 검색어
  const [filteredUsers, setFilteredUsers] = useState([]); // 필터된 사용자 목록
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showAddToken, setShowAddToken] = useState(false);
  const [customTokens, setCustomTokens] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // 전송 모달 관련 상태
  const [showTransModal, setShowTransModal] = useState(false);
  
  // Swap/Buy view states - moved to top level to avoid conditional hooks
  const [fromToken, setFromToken] = useState('USDT');
  const [toToken, setToToken] = useState('wHANDY');
  const [swapAmount, setSwapAmount] = useState('');
  const [swapPin, setSwapPin] = useState('');
  const [handyPrice, setHandyPrice] = useState<number | null>(null);
  const [handyChange24h, setHandyChange24h] = useState<number>(0);
  // 재매입 참여 중 표시용 (2026-10-02)
  const [royalJoined, setRoyalJoined] = useState(0);
  const [royalDaily, setRoyalDaily] = useState(0);
  const [showLockup, setShowLockup] = useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('handy-jwt-token');
        if (!t) return;
        const j = await fetch('/api/royal/info', { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json());
        const act = (j?.myDeposits || []).filter((d: any) => d.status === 'active');
        setRoyalJoined(act.reduce((x: number, d: any) => x + Number(d.amount || 0), 0));
        setRoyalDaily(act.reduce((x: number, d: any) => x + Number(d.daily || 0), 0));
      } catch { /* 표시용이라 실패해도 무시 */ }
    })();
  }, [user?.walletAddress]);
  const [usdtBalance, setUsdtBalance] = useState(0);
  const [depositTxHash, setDepositTxHash] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);

  // CoinGecko 기반 실시간 HANDY 시세 (60초마다 갱신)
  React.useEffect(() => {
    const fetchPrice = () => {
      fetch('/api/price/handy')
        .then(r => r.json())
        .then(d => {
          if (typeof d.usd === 'number') setHandyPrice(d.usd);
          if (typeof d.change24h === 'number') setHandyChange24h(d.change24h);
        })
        .catch(() => {});
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 60000);
    return () => clearInterval(interval);
  }, []);

  // 내부 원장 USDT 잔액 조회
  const refreshUsdtBalance = React.useCallback(() => {
    if (!user?.walletAddress) return;
    fetch(`/api/usdt/balance/${user.walletAddress}`)
      .then(r => r.json())
      .then(d => { if (typeof d.usdtBalance === 'number') setUsdtBalance(d.usdtBalance); })
      .catch(() => {});
  }, [user?.walletAddress]);

  React.useEffect(() => { refreshUsdtBalance(); }, [refreshUsdtBalance]);

  // 이더리움에서 트레저리로 보낸 USDT 트랜잭션 검증 후 내부 원장 적립
  const handleDepositUsdt = async () => {
    // USDT 입금 확인 기능은 정식 오픈 전까지 비활성화 (2026-09-11)
    toast.info('USDT 입금 기능은 준비 중입니다.');
    return;
    // eslint-disable-next-line no-unreachable
    if (!depositTxHash) { toast.error('트랜잭션 해시를 입력하세요.'); return; }
    setDepositLoading(true);
    try {
      const res = await fetch('/api/usdt/deposit', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ethTxHash: depositTxHash, address: user?.walletAddress })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`${data.amount} USDT 입금 확인 완료`);
        setDepositTxHash('');
        refreshUsdtBalance();
      } else {
        toast.error(data.error || 'USDT 입금 확인 실패');
      }
    } catch {
      toast.error('입금 확인 서버 연결 실패');
    } finally {
      setDepositLoading(false);
    }
  };

  // Generate QR code when needed
  React.useEffect(() => {
    if ((activeView === 'receive' || activeView === 'qr') && user?.walletAddress) {
      generateQRCode(user.walletAddress).then(setQrCodeUrl);
    }
  }, [activeView, user?.walletAddress]);

  // 재단 지갑을 기본값으로 설정
  React.useEffect(() => {
    if (isFoundationMode && !senderWallet) {
      // Handy 재단 지갑 정보 설정
      const foundationWallet = {
        id: 'foundation',
        nickname: tb('foundationWallet'),
        username: 'Handy Foundation',
        wallet_address: '0xFoundationWallet123456789',
        balance: 1000000, // 재단 지갑 기본 잔고
        is_active: true
      };
      setSenderWallet(foundationWallet);
      setSenderBalance(foundationWallet.balance);
    }
  }, [isFoundationMode, senderWallet]);

  // 대기 중인 회원가입 에어드롭도 보유 수량에 합산해 표시(사용은 추천코드 인증 후)
  const pendingBonus = Number(user?.pendingSignupBonus || 0);
  const handyHoldings = Number(user?.balance || 0) + pendingBonus + lockedHandy;
  // 총보유에는 락업이 섞여 있다. 실제로 지금 쓸 수 있는 건 이것뿐이다.
  const handyAvailable = Number(user?.balance || 0) + pendingBonus;

  const tokens: Token[] = [
    {
      symbol: 'wHANDY',
      name: 'Handy Token',
      balance: handyHoldings,
      value: handyHoldings * (handyPrice || 0),
      change: handyChange24h,
      icon: 'H'
    },
    {
      symbol: 'USDT',
      name: 'Tether USD',
      balance: usdtBalance,
      value: usdtBalance,
      change: 0,
      icon: '$'
    }
  ];

  const totalPortfolioValue = tokens.reduce((sum, token) => sum + token.value, 0);

  const handleSendTokens = async () => {
    if (!amount || !address) {
      toast.error('Please enter both amount and address.');
      return;
    }

    const transferAmount = parseFloat(amount);
    if (transferAmount <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }

    if (transferAmount > user.balance) {
      toast.error('Insufficient balance.');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      const response = await fetch('/api/blockchain/vttn/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          toAddress: address,
          amount: transferAmount,
          tokenType: 'wHANDY'
        })
      });

      if (!response.ok) {
        throw new Error('전송 실패');
      }

      const result = await response.json();
      
      // 잔고 업데이트
      const newBalance = (user?.balance || 0) - transferAmount;
      await onUpdateBalance(newBalance);
      
      // 트랜잭션 추가
      const newTransaction = {
        id: Date.now(),
        type: 'Transfer Sent',
        amount: -transferAmount,
        createdAt: new Date().toISOString()
      };
      await onAddTransaction(newTransaction);
      
      toast.success(`Successfully sent ${transferAmount} wHANDY`);
      setAmount('');
      setAddress('');
      setActiveView('main');
    } catch (error) {
      console.error('전송 오류:', error);
      toast.error('Transfer failed.');
    } finally {
      setIsLoading(false);
    }
  };



  // [2026-09-11] 지갑 액션(Send/Receive/Swap/QR)은 전부 정식 오픈 전이다.
  // 지금 열어두면 사용자가 실제 자산을 움직여버릴 수 있으므로 화면에는 "오픈 예정"으로
  // 두고 눌러도 안내 토스트만 띄운다. 잔액·입금내역 조회는 그대로 열어둔다.
  const notifyComingSoon = (label: string) => {
    toast.info(`${label} 기능은 오픈 예정입니다.`);
  };

  // 상단 Trans 버튼 클릭 시 토큰 전송 페이지로 이동
  const handleSendClick = () => {
    notifyComingSoon('보내기');
  };

  // TransModal에서 전송 완료 시 호출
  const handleTransferComplete = (transferData: any) => {
    setShowTransModal(false);
    // 실제 전송 로직은 TransModal에서 처리
    toast.success(i18n.t('transferCompleted'));
  };

  // 전체 유저 목록 조회
  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      const response = await fetch('/api/admin/users', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('유저 목록 조회 실패');
      }

      const users = await response.json();
      setAllUsers(users);
    } catch (error) {
      console.error('유저 목록 조회 오류:', error);
      toast.error(i18n.t('cannotLoadUsers'));
    }
  };

  // 재단 지갑으로 토큰 전송
  const handleFoundationTransfer = async () => {
    if (!foundationAmount) {
      toast.error(i18n.t('transferAmountRequired'));
      return;
    }

    if (!senderWallet) {
      toast.error(i18n.t('selectSenderWallet'));
      return;
    }

    if (!receiverWallet) {
      toast.error(i18n.t('selectReceiverWallet'));
      return;
    }

    const transferAmount = parseFloat(foundationAmount);
    if (transferAmount <= 0) {
      toast.error(i18n.t('enterValidAmount'));
      return;
    }

    // 잔고 확인
    if (senderBalance < transferAmount) {
      toast.error(i18n.t('insufficientFunds'));
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      // [2026-10-21] 재단 전송은 출금 번호까지 맞아야 나간다.
      // 번호를 안 보내서 서버가 계속 400 을 돌려줬고, 화면엔 "토큰 전송 실패"만 떴다.
      const withdrawPin = window.prompt('재단 출금 번호를 입력하세요');
      if (!withdrawPin) { setIsLoading(false); return; }

      const response = await fetch('/api/admin/foundation/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: transferAmount,
          tokenType: 'wHANDY',
          toAddress: receiverWallet.wallet_address,
          userIds: [receiverWallet.id],
          withdrawPin,
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.success === false) {
        // 서버가 알려준 사유를 그대로 띄운다 — "토큰 전송 실패" 한 줄로는 원인을 못 찾는다
        throw new Error(result?.error || `토큰 전송 실패 (${response.status})`);
      }
      
      toast.success(tr('transferSuccess', { amount: transferAmount, recipient: receiverWallet.nickname || receiverWallet.wallet_address }));
      
      // 잔고 업데이트
      setSenderBalance(prev => prev - transferAmount);
      setReceiverBalance(prev => prev + transferAmount);
      
      setFoundationAmount('');
      setActiveView('main');
    } catch (error) {
      console.error('재단 지갑 전송 오류:', error);
      toast.error(i18n.t('foundationTransferFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyWithUSDT = async () => {
    if (!swapAmount || parseFloat(swapAmount) <= 0) {
      toast.error('올바른 수량을 입력하세요.');
      return;
    }
    const direction = fromToken === 'USDT' ? 'usdt2handy' : 'handy2usdt';
    if (direction === 'handy2usdt' && (!swapPin || swapPin.length < 6)) {
      toast.error('지갑 비밀번호(6자리 이상)를 입력하세요.');
      return;
    }
    if (!user?.walletAddress) {
      toast.error(i18n.t('authRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const body: Record<string, any> = { direction, amount: swapAmount, address: user.walletAddress };
      if (direction === 'handy2usdt') body.pin = swapPin;

      const response = await fetch('/api/usdt/swap', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || '스왑 실패');
      }

      if (direction === 'usdt2handy') {
        await onUpdateBalance((user?.balance || 0) + result.handyAmount);
        setUsdtBalance(prev => prev - result.usdtAmount);
      } else {
        await onUpdateBalance((user?.balance || 0) - result.handyAmount);
        setUsdtBalance(prev => prev + result.usdtAmount);
      }

      await onAddTransaction({
        id: Date.now(),
        type: direction === 'usdt2handy' ? 'wHANDY Purchase' : 'wHANDY Sale',
        amount: direction === 'usdt2handy' ? result.handyAmount : -result.handyAmount,
        createdAt: new Date().toISOString()
      });

      toast.success(`스왑 완료: ${result.usdtAmount.toFixed(2)} USDT ↔ ${result.handyAmount.toFixed(2)} wHANDY`);
      setSwapAmount('');
      setSwapPin('');
      setActiveView('main');
    } catch (error: any) {
      console.error('스왑 오류:', error);
      toast.error(error.message || '스왑 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(user?.walletAddress || '');
      toast.success('Address copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy address.');
    }
  };

  const handleWalletConnect = (walletName: string) => {
    setConnectedWallet(walletName);
    setShowWalletConnect(false);
  };

  const handleTokenClick = (token: Token) => {
    setSelectedToken(token);
    setActiveView('token-details');
  };

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleAddToken = (token: Token) => {
    setCustomTokens(prev => [...prev, token]);
  };

  // Combined tokens list
  const allTokens = [...tokens, ...customTokens];

  // Helper functions for swap functionality
  const swapTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setSwapAmount('');
  };

  const calculateSwapAmount = (inputAmount: string) => {
    if (!inputAmount || !handyPrice) return '0.00';
    const input = parseFloat(inputAmount);
    if (fromToken === 'USDT' && toToken === 'wHANDY') {
      return (input / handyPrice).toFixed(2);
    } else if (fromToken === 'wHANDY' && toToken === 'USDT') {
      return (input * handyPrice).toFixed(2);
    }
    return '0.00';
  };

  // Generate QR code using qrcode library
  const generateQRCode = async (text: string) => {
    try {
      // Use dynamic import for QR code generation
      const QRCode = await import('qrcode');
      return await QRCode.toDataURL(text, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
    } catch (error) {
      console.warn('QR Code generation failed, using fallback:', error);
      // Fallback to a simple pattern without dynamic import
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      
      canvas.width = 200;
      canvas.height = 200;
      
      // Create a simple QR-like pattern
      const size = 8;
      const modules = 25;
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 200, 200);
      
      ctx.fillStyle = '#000000';
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        hash = ((hash << 5) - hash + text.charCodeAt(i)) & 0xffffffff;
      }
      
      for (let x = 0; x < modules; x++) {
        for (let y = 0; y < modules; y++) {
          const index = x * modules + y;
          if ((hash >> (index % 32)) & 1) {
            ctx.fillRect(x * size, y * size, size, size);
          }
        }
      }
      
      // Add finder patterns (corners)
      const drawFinderPattern = (startX: number, startY: number) => {
        ctx.fillStyle = '#000000';
        ctx.fillRect(startX, startY, 7 * size, 7 * size);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(startX + size, startY + size, 5 * size, 5 * size);
        ctx.fillStyle = '#000000';
        ctx.fillRect(startX + 2 * size, startY + 2 * size, 3 * size, 3 * size);
      };
      
      drawFinderPattern(0, 0);
      drawFinderPattern((modules - 7) * size, 0);
      drawFinderPattern(0, (modules - 7) * size);
      
      return canvas.toDataURL();
    }
  };

  // QR View
  if (activeView === 'usdt-deposit') {
    return <UsdtDeposit onBack={() => setActiveView('main')} />;
  }

  if (activeView === 'usdt-withdraw') {
    return <UsdtWithdraw onBack={() => setActiveView('main')} />;
  }

  if (activeView === 'qr') {
    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">My QR Code</h1>
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
              <QrCode className="h-5 w-5 text-white" />
            </div>
          </div>

          <Card className="bg-card border-border">
            <CardContent className="p-6 text-center">
              <div className="relative mb-6">
                <div className="w-64 h-64 bg-white border-2 border-green-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                  {qrCodeUrl ? (
                    <img 
                      src={qrCodeUrl}
                      alt="Wallet Address QR Code"
                      className="w-60 h-60 rounded-xl"
                    />
                  ) : (
                    <div className="w-60 h-60 bg-gray-200 rounded-xl flex items-center justify-center">
                      <QrCode className="w-32 h-32 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-green-500 to-blue-500 rounded-full"></div>
                <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-green-400 to-blue-400 rounded-full"></div>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-4 mb-4">
                <p className="text-green-700 dark:text-green-300 font-medium mb-2">My Wallet Address</p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-700">
                  <p className="text-foreground text-sm font-mono break-all">
                    {user?.walletAddress || 'No wallet address'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Network</p>
                  <p className="text-foreground font-medium">Ethereum</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Balance</p>
                  <p className="text-green-600 font-medium">{(user?.balance || 0).toFixed(2)} wHANDY</p>
                </div>
              </div>

              <Button
                onClick={copyAddress}
                className="w-full h-12 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Address
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Token Details View
  if (activeView === 'token-details' && selectedToken) {
    console.log('🎨 TokenDetails로 전달하는 transactions:', transactions);
    return (
      <TokenDetails
        token={selectedToken}
        transactions={transactions}
        grants={selectedToken?.symbol === 'wHANDY' ? grants : []}
        lockedAmount={selectedToken?.symbol === 'wHANDY' ? lockedHandy : 0}
        lockSchedule={selectedToken?.symbol === 'wHANDY' ? lockSchedule : []}
        availableAmount={selectedToken?.symbol === 'wHANDY' ? handyAvailable : 0}
        onBack={() => setActiveView('main')}
        onSend={handleSendClick}
        onReceive={() => notifyComingSoon('받기')}
        onBuy={() => {
          // 어드민이 아직 안 열었으면 기존처럼 준비 중 안내만 한다
          if (!buyEnabled) { notifyComingSoon('구매'); return; }
          // 시세는 팝업을 열 때 최신으로 받아온다(오래된 값으로 사면 안 된다)
          fetch('/api/usdt/price')
            .then(r => (r.ok ? r.json() : null))
            .then(d => setBuyPrice(d?.price ?? null))
            .catch(() => setBuyPrice(null));
          setShowBuyModal(true);
        }}
      />
    );
  }

  // Send View
  if (activeView === 'send') {
    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Send Tokens</h1>
            <div className="w-10 h-10"></div>
          </div>

          <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
            <CardContent className="p-6 text-center">
              <p className="text-white/80 text-sm mb-1">Available Balance</p>
              <p className="text-3xl font-light">{(user?.balance || 0).toFixed(2)}</p>
              <p className="text-white/60 text-sm">wHANDY</p>
            </CardContent>
          </Card>

          {/* 재단 지갑 모드 토글 */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-foreground">{i18n.t('walletTransferTitle')}</h3>
                  <p className="text-sm text-muted-foreground">{i18n.t('walletTransferDesc')}</p>
                </div>
                <button
                  onClick={() => setIsFoundationMode(!isFoundationMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isFoundationMode ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isFoundationMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6 space-y-4">
              {!isFoundationMode ? (
                <>
                  <div>
                    <Label htmlFor="address">Recipient Address</Label>
                    <Input
                      id="address"
                      placeholder="Enter wallet address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="mt-1 border-border bg-input-background"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="amount">Amount (wHANDY)</Label>
                    <Input
                      id="amount"
                      type="number"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="mt-1 border-border bg-input-background"
                    />
                  </div>

                  <Button 
                    onClick={handleSendTokens}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white h-12"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                    ) : (
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                    )}
                    {isLoading ? 'Sending...' : 'Send Tokens'}
                  </Button>
                </>
              ) : (
                <>
                  {/* 재단 지갑 전송 모드 UI - 원래 스타일로 복구 */}
                  <div>
                    <Label htmlFor="foundation-address">Recipient Address</Label>
                    <Input
                      id="foundation-address"
                      placeholder="Enter wallet address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="mt-1 border-border bg-input-background"
                    />
                  </div>

                  {/* 하단: 전송 금액 및 전송 버튼 */}
                  <div className="space-y-4 mt-6 pt-4 border-t">
                    <div>
                      <Label htmlFor="foundationAmount">{i18n.t('transferAmountLabel')}</Label>
                      <Input
                        id="foundationAmount"
                        type="number"
                        placeholder="0.00"
                        value={foundationAmount}
                        onChange={(e) => setFoundationAmount(e.target.value)}
                        className="mt-1 border-border bg-input-background"
                      />
                    </div>

                    <Button 
                      onClick={handleFoundationTransfer}
                      className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white h-12"
                      disabled={isLoading || !senderWallet || !receiverWallet || !foundationAmount}
                    >
                      {isLoading ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      ) : (
                        <ArrowUpRight className="w-4 h-4 mr-2" />
                      )}
                      {isLoading ? 'Sending...' : tr('transferFromTo', { from: senderWallet?.nickname || i18n.t('senderWallet'), to: receiverWallet?.nickname || i18n.t('receiverWallet') })}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-3">Recent Transactions</h4>
              {(() => {
                console.log('🎨 Send 화면 - transactions:', transactions?.length || 0);
                
                if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>{i18n.t('noRecentTransactions')}</p>
                      <p className="text-sm">{i18n.t('transactionsWillAppear')}</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-green-500 mb-2">{tr('transactionCount', { count: transactions.length })}</p>
                    {transactions.slice(0, 5).map((tx, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-accent transition-colors">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {tx.from?.substring(0, 10)}...{tx.from?.substring(38)} → {tx.to?.substring(0, 10)}...
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.timestamp * 1000).toLocaleString()} | {i18n.t('blockLabel')} #{tx.blockNumber}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${tx.to === user?.walletAddress ? 'text-green-500' : 'text-red-500'}`}>
                            {tx.to === user?.walletAddress ? '+' : '-'}{parseFloat(tx.value).toFixed(2)} wHANDY
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Receive View
  if (activeView === 'receive') {
    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Receive {selectedToken?.symbol || 'Tokens'}</h1>
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <ArrowDownLeft className="h-5 w-5 text-white" />
            </div>
          </div>

          <Card className="bg-card border-border border-purple-200 dark:border-purple-800">
            <CardContent className="p-6 text-center">
              <div className="relative mb-6">
                <div className="w-48 h-48 bg-white border-2 border-purple-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                  {qrCodeUrl ? (
                    <img 
                      src={qrCodeUrl}
                      alt="Wallet Address QR Code"
                      className="w-44 h-44 rounded-xl"
                    />
                  ) : (
                    <div className="w-44 h-44 bg-gray-200 rounded-xl flex items-center justify-center">
                      <QrCode className="w-24 h-24 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
                <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"></div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 mb-4">
                <p className="text-purple-700 dark:text-purple-300 font-medium mb-2">Your Wallet Address</p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                  <p className="text-foreground text-sm font-mono break-all">
                    {user?.walletAddress || 'No wallet address'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Network</p>
                  <p className="text-foreground font-medium">{selectedToken?.symbol === 'USDT' ? 'Ethereum' : 'Handy Mainnet'}</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Balance</p>
                  <p className="text-green-600 font-medium">{(selectedToken?.balance ?? (user?.balance || 0)).toFixed(2)} {selectedToken?.symbol || 'wHANDY'}</p>
                </div>
              </div>
              {selectedToken?.symbol === 'USDT' && (
                <p className="text-xs text-muted-foreground mb-4">
                  이 주소로 이더리움 USDT(ERC-20)를 보내면, 입금 확인 후 지갑의 USDT 잔액에 반영됩니다.
                </p>
              )}

              <Button
                onClick={copyAddress}
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Address
              </Button>
            </CardContent>
          </Card>

          {/* Recent Receive History */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-3">Recent Transactions</h4>
              {(() => {
                console.log('🎨 UI 렌더링 - transactions:', transactions);
                console.log('🎨 UI 렌더링 - transactions.length:', transactions?.length);
                console.log('🎨 UI 렌더링 - Array.isArray:', Array.isArray(transactions));
                
                if (!transactions) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>⚠️ transactions = null</p>
                    </div>
                  );
                }
                
                if (!Array.isArray(transactions)) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>⚠️ transactions가 배열이 아닙니다</p>
                      <p className="text-xs">{typeof transactions}</p>
                    </div>
                  );
                }
                
                if (transactions.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>{i18n.t('noRecentReceives')}</p>
                      <p className="text-sm">{i18n.t('transactionsWillAppear')}</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-green-500">{tr('transactionCount', { count: transactions.length })}</p>
                    {transactions.slice(0, 5).map((tx, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {tx.from?.substring(0, 10)}...{tx.from?.substring(38)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.timestamp * 1000).toLocaleString()}
                          </p>
                          <p className="text-xs text-blue-500">{i18n.t('blockLabel')} #{tx.blockNumber}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-green-500">+{parseFloat(tx.value).toFixed(2)} wHANDY</p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Swap View (Uniswap style)
  if (activeView === 'buy') {

    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Swap Tokens</h1>
            <div className="w-10 h-10"></div>
          </div>

          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-2">
              <p className="text-sm font-medium">USDT 입금 (Ethereum → 트레저리)</p>
              <div className="flex space-x-2">
                <Input
                  placeholder="0x... (USDT 전송 트랜잭션 해시)"
                  value={depositTxHash}
                  onChange={(e) => setDepositTxHash(e.target.value)}
                  className="text-xs"
                />
                <Button onClick={handleDepositUsdt} disabled={depositLoading || !depositTxHash} className="whitespace-nowrap">
                  {depositLoading ? '확인중...' : '입금 확인'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">보유 USDT: {usdtBalance.toFixed(2)}</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6 space-y-4">
              {/* From Token */}
              <div className="bg-muted rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-muted-foreground text-sm">From</p>
                  <p className="text-muted-foreground text-sm">Balance: {fromToken === 'USDT' ? usdtBalance.toFixed(2) : (user?.balance || 0).toFixed(2)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Input
                    type="number"
                    placeholder="0.0"
                    value={swapAmount}
                    onChange={(e) => setSwapAmount(e.target.value)}
                    className="text-2xl font-bold bg-transparent border-0 p-0 h-auto"
                  />
                  <div className="flex items-center space-x-2 bg-background rounded-full px-3 py-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {fromToken === 'USDT' ? '₮' : 'V'}
                    </div>
                    <span className="font-bold">{fromToken}</span>
                  </div>
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center">
                <button
                  onClick={swapTokens}
                  className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg"
                >
                  <RefreshCw className="w-6 h-6" />
                </button>
              </div>

              {/* To Token */}
              <div className="bg-muted rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-muted-foreground text-sm">To</p>
                  <p className="text-muted-foreground text-sm">Balance: {toToken === 'USDT' ? usdtBalance.toFixed(2) : (user?.balance || 0).toFixed(2)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <p className="text-2xl font-bold">{calculateSwapAmount(swapAmount)}</p>
                  </div>
                  <div className="flex items-center space-x-2 bg-background rounded-full px-3 py-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {toToken === 'USDT' ? '₮' : 'V'}
                    </div>
                    <span className="font-bold">{toToken}</span>
                  </div>
                </div>
              </div>

              {/* Exchange Rate */}
              {handyPrice && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Exchange Rate</span>
                    <span className="font-bold">1 wHANDY = ${handyPrice.toFixed(5)} (CoinGecko)</span>
                  </div>
                </div>
              )}

              {fromToken === 'wHANDY' && (
                <Input
                  type="password"
                  maxLength={12}
                  placeholder="지갑 비밀번호 (로그인 비밀번호와 같습니다)"
                  value={swapPin}
                  onChange={(e) => setSwapPin(e.target.value)}
                  className="text-sm"
                />
              )}

              <Button 
                onClick={handleBuyWithUSDT}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white h-12"
                disabled={isLoading || !swapAmount}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {isLoading ? 'Swapping...' : `Swap ${fromToken} for ${toToken}`}
              </Button>
            </CardContent>
          </Card>

          {/* Recent Swap Transactions */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-3">Recent Transactions</h4>
              {(() => {
                console.log('🎨 Swap 화면 - transactions:', transactions?.length || 0);
                
                if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>{i18n.t('noRecentSwaps')}</p>
                      <p className="text-sm">{i18n.t('transactionsWillAppear')}</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-green-500 mb-2">{tr('transactionCount', { count: transactions.length })}</p>
                    {transactions.slice(0, 5).map((tx, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-accent transition-colors">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {tx.from?.substring(0, 10)}... → {tx.to?.substring(0, 10)}...
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.timestamp * 1000).toLocaleString()} | {i18n.t('blockLabel')} #{tx.blockNumber}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${tx.to === user?.walletAddress ? 'text-green-500' : 'text-red-500'}`}>
                            {tx.to === user?.walletAddress ? '+' : '-'}{parseFloat(tx.value).toFixed(2)} wHANDY
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main View (Wallet home)
  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Handy Wallet</h1>
            <p className="text-muted-foreground text-sm">Secure Digital Asset Management</p>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setShowWalletConnect(true)}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <Link className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Portfolio Card */}
        <Card className="bg-gradient-to-br from-blue-600 via-purple-600 to-blue-500 border-0 text-white relative overflow-hidden">
          <CardContent className="p-6 relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-white/90 text-base mb-2">{i18n.t('totalBalance')}</p>
                <p className="text-4xl font-bold mb-1">
                  {(balanceView === 'vttn' ? handyHoldings : totalPortfolioValue)
                    .toLocaleString('ko-KR', { maximumFractionDigits: 2 })}
                  {balanceView === 'vttn' ? ' wHANDY' : ' USD'}
                </p>
                <p className="text-white/70 text-sm flex items-center">
                  {handyChange24h >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                  코인 시세 {handyChange24h >= 0 ? '+' : ''}{Math.abs(handyChange24h) < 0.01 ? handyChange24h.toFixed(3) : handyChange24h.toFixed(2)}% (24h)
                </p>
              </div>
              <button
                onClick={() => setBalanceView(balanceView === 'usd' ? 'vttn' : 'usd')}
                className="text-white/80 hover:text-white transition-colors p-2 rounded-full bg-white/10 hover:bg-white/20"
              >
                <Wallet className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex items-end justify-between text-sm">
              <div>
                {lockedHandy > 0 && (
                  <button onClick={() => setShowLockup(true)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                             background: 'rgba(255,255,255,.16)', color: '#fff',
                             border: 0, borderRadius: 999, padding: '8px 14px',
                             fontSize: 13, fontWeight: 700 }}>
                    🔒 락업 {lockedHandy.toLocaleString('ko-KR', { maximumFractionDigits: 0 })} ›
                  </button>
                )}
              </div>
              <div className="text-right">
                <p className="text-white/80 text-base">USD 환산</p>
                <p className="text-white font-bold text-lg">
                  ${handyPrice ? (handyHoldings * handyPrice).toLocaleString('ko-KR', { maximumFractionDigits: 2 }) : '...'}
                </p>
                {handyPrice && (
                  <p className="text-white/90 text-sm mt-0.5">
                    1 wHANDY = ${handyPrice.toFixed(5)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
          
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
        </Card>

        <LockupModal open={showLockup} onClose={() => setShowLockup(false)}
          locked={lockedHandy} schedule={lockSchedule} />


        {/* Action Buttons - 전부 오픈 예정(2026-09-11). 색상은 Tailwind 퍼지에 걸리지
            않도록 인라인 style로 고정한다. */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { key: 'send', label: withdrawEnabled ? 'USDT 출금' : '보내기', icon: <ArrowUpRight className="h-6 w-6" /> },
            { key: 'receive', label: bscEnabled ? 'USDT 입금' : '받기', icon: <ArrowDownLeft className="h-6 w-6" /> },
            { key: 'swap', label: '스왑', icon: <RefreshCw className="h-6 w-6" /> },
            { key: 'qr', label: 'QR 코드', icon: <QrCode className="h-6 w-6" /> },
          ].map(act => (
            <Button
              key={act.key}
              onClick={() => {
                // 받기(USDT 입금)만 오픈. 나머지는 아직 오픈 예정 그대로 둔다.
                if (act.key === 'receive' && bscEnabled) { setActiveView('usdt-deposit'); return; }
                if (act.key === 'send' && withdrawEnabled) { setActiveView('usdt-withdraw'); return; }
                notifyComingSoon(act.label);
              }}
              className="h-24 flex flex-col items-center justify-center space-y-1 rounded-2xl shadow-sm"
              style={
                (act.key === 'receive' && bscEnabled) || (act.key === 'send' && withdrawEnabled)
                  ? { background: '#FF6B00', color: '#FFFFFF' }
                  : { background: '#E5E7EB', color: '#9CA3AF' }
              }
            >
              {act.icon}
              <span className="text-xs font-medium">{act.label}</span>
              {!((act.key === 'receive' && bscEnabled) || (act.key === 'send' && withdrawEnabled)) && (
                <span className="text-[10px]" style={{ color: '#6B7280' }}>오픈 예정</span>
              )}
            </Button>
          ))}
        </div>

        {/* Token List */}
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <Wallet className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg">My Assets</h3>
                  <p className="text-muted-foreground text-sm">{allTokens.length} tokens</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddToken(true)}
                className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center hover:from-blue-600 hover:to-purple-600 transition-all duration-200 shadow-lg"
              >
                <Plus className="h-5 w-5 text-white" />
              </button>
            </div>

            <div className="space-y-4">
              {allTokens.map((token, index) => (
                <button
                  key={index}
                  onClick={() => handleTokenClick(token)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {token.icon}
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-foreground text-lg">{token.symbol}</p>
                      <p className="text-muted-foreground text-sm">{token.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-foreground text-lg">{token.balance.toFixed(2)}</p>
                    <div className="flex items-center justify-end space-x-2">
                      <p className="text-muted-foreground text-base">${token.value.toFixed(2)}</p>
                      {token.symbol !== 'USDT' && (
                        <div className="flex items-center space-x-1">
                          <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                            token.change >= 0 
                              ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' 
                              : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
                          }`}>
                            {token.change >= 0 ? '+' : ''}{Math.abs(token.change) < 0.01 ? token.change.toFixed(3) : token.change.toFixed(2)}%
                          </span>
                          {token.change >= 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-600" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <WalletConnectModal
        isOpen={showWalletConnect}
        onClose={() => setShowWalletConnect(false)}
        onConnect={handleWalletConnect}
      />

      <AddTokenModal
        isOpen={showAddToken}
        onClose={() => setShowAddToken(false)}
        onAdd={handleAddToken}
      />

      <TransactionDetailsModal
        isOpen={showTransactionDetails}
        onClose={() => setShowTransactionDetails(false)}
        transaction={selectedTransaction}
        handyPrice={handyPrice}
      />



      <BuyConfirmModal



        open={showBuyModal}



        onClose={() => setShowBuyModal(false)}



        usdtBalance={Number(usdtBalance || 0)}
        depositAddress={user?.walletAddress}



        price={buyPrice}



        feePercent={buyFee}



        lock={buyLock}



        onConfirm={async (usdtAmount: number) => {



          try {



            const r = await fetch('/api/usdt/swap-request', {



              method: 'POST',



              headers: { 'Content-Type': 'application/json', ...authHeaders() },



              body: JSON.stringify({ usdtAmount }),



              credentials: 'include',



            });



            const d = await r.json();



            if (!d.success) { toast.error(d.error || '구매 실패'); return; }



            if (d.status === 'completed') {



              toast.success(`구매 완료: ${Number(d.handyAmount).toLocaleString(undefined, { maximumFractionDigits: 4 })} wHANDY`);



            } else {



              toast.success('구매 신청이 접수되었습니다. 확인 후 지급됩니다.');



            }



            setShowBuyModal(false);



          } catch (e: any) {



            toast.error(e?.message || '구매 실패');



          }



        }}



      />



      <TransModal
        isOpen={showTransModal}
        onClose={() => setShowTransModal(false)}
        user={user}
        onUpdateBalance={onUpdateBalance}
        onAddTransaction={onAddTransaction}
      />
    </div>
  );
}